import type { PlainTextResolver } from './plain-text';
import { toPlainText } from './plain-text';
import type { BlockNode, ListItemNode, RichTextDoc } from './schema';

/**
 * The lines of a message that read as a list (D-1120).
 *
 * This is what "turn this message into fifty tasks" needs: somebody writes a list in chat, and
 * each line of it is a separate piece of work. Pure, so the server, the web app and the phone all
 * agree on what counts as a line — the alternative is three different answers to "how many tasks
 * is this?", which is exactly the kind of thing people notice.
 *
 * ── WHAT COUNTS AS AN ITEM ──────────────────────────────────────────────────
 *
 * A `listItem` of a bulletList or orderedList, at ANY depth. Nesting is flattened deliberately:
 * somebody writing
 *
 *     - Launch the site
 *       - copy
 *       - images
 *
 * means three pieces of work, not one. Sub-tasks are a structure this product has (a task can
 * block another), but inferring them from indentation would be guessing at intent, and guessing
 * wrong silently is worse than a flat list the person can edit.
 *
 * A list item's own nested list is NOT included in its text — only its first paragraph is — so
 * the parent reads "Launch the site" rather than "Launch the site copy images".
 *
 * ── WHAT IS NOT AN ITEM ─────────────────────────────────────────────────────
 *
 * Plain paragraphs, headings, quotes and code. A message that is prose with one bullet in it has
 * one item, not a paragraph pretending to be work. `fromParagraphs` opts into treating hard-broken
 * lines of a single paragraph as items, for the very common case of somebody typing a list with
 * Shift+Enter and no bullets at all — the caller asks for that explicitly, because turning every
 * multi-line message into a pile of tasks by default would be a nasty surprise.
 */

export interface RichTextListItem {
  /** The item's own text, mentions and links resolved by the caller's resolver. */
  text: string;
  /** Depth, 0 for a top-level item. Kept so a caller can show the shape it flattened. */
  depth: number;
  /** True when it came from a numbered list. */
  ordered: boolean;
}

/** The blocks of a list item, excluding any list nested inside it. */
function ownBlocks(item: ListItemNode): BlockNode[] {
  return (item.content ?? []).filter(
    (block) => block.type !== 'bulletList' && block.type !== 'orderedList',
  );
}

function walk(
  nodes: readonly BlockNode[],
  depth: number,
  ordered: boolean,
  resolver: PlainTextResolver,
  out: RichTextListItem[],
): void {
  for (const node of nodes) {
    if (node.type === 'bulletList' || node.type === 'orderedList') {
      const isOrdered = node.type === 'orderedList';
      for (const item of node.content ?? []) {
        const own = ownBlocks(item);
        // `toPlainText` wants a doc; a synthetic one around the item's own blocks is the cheapest
        // way to reuse the one definition of "what this text says" (mentions, links, emoji).
        const text = toPlainText({ type: 'doc', content: own } as RichTextDoc, resolver).trim();
        if (text) out.push({ text, depth, ordered: isOrdered });
        // Then whatever this item nests, one level deeper.
        walk(item.content ?? [], depth + 1, isOrdered, resolver, out);
      }
      continue;
    }
    if (node.type === 'blockquote') {
      // A quoted list is somebody quoting somebody else's list. Still a list.
      walk(node.content ?? [], depth, ordered, resolver, out);
    }
  }
}

/** Every list line in the document, in reading order. */
export function listItemsOf(
  doc: RichTextDoc,
  options: { resolver?: PlainTextResolver; fromParagraphs?: boolean } = {},
): RichTextListItem[] {
  const resolver = options.resolver ?? {};
  const out: RichTextListItem[] = [];
  walk(doc.content ?? [], 0, false, resolver, out);
  if (out.length > 0 || !options.fromParagraphs) return out;

  /*
   * No list nodes, and the caller asked for the fallback: treat a single paragraph's hard breaks
   * as lines. This is how most people actually type a list in a chat box — Shift+Enter, no
   * bullets — and refusing to see it would make the feature feel broken for the commonest input.
   *
   * Only when there are at least two lines, so a one-line message never becomes "a list of one".
   */
  const text = toPlainText(doc, resolver);
  const lines = text
    .split('\n')
    .map((line) => line.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, '').trim())
    .filter((line) => line.length > 0);
  if (lines.length < 2) return [];
  return lines.map((line) => ({ text: line, depth: 0, ordered: false }));
}
